import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
  Grid,
  Alert,
  Snackbar,
  Tooltip,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  MoreVert,
  Person,
  Security,
  AdminPanelSettings,
  Build,
  Warehouse,
  SupervisorAccount,
  SupportAgent,
  PersonAdd,
  Lock,
  LockOpen,
  Search,
  Clear,
} from '@mui/icons-material';
import axios from 'axios';
import { mockApi } from '../../services/mockData';

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  email: string;
  role: 'installer' | 'warehouse' | 'manager' | 'admin' | 'sales_rep' | 'super_admin';
  status: 'active' | 'inactive' | 'pending';
  lastLogin?: string;
  createdAt: string;
  permissions: string[];
}

interface RolePermissions {
  installer: string[];
  warehouse: string[];
  manager: string[];
  admin: string[];
  sales_rep: string[];
  super_admin: string[];
}

const rolePermissions: RolePermissions = {
  installer: ['view_installs', 'update_install_status', 'view_schedule'],
  warehouse: ['view_inventory', 'manage_inventory', 'view_orders'],
  manager: ['view_all', 'manage_team', 'view_reports', 'manage_schedule'],
  admin: ['view_all', 'manage_users', 'manage_settings', 'view_reports'],
  sales_rep: ['view_customers', 'create_proposals', 'manage_customers', 'view_schedule'],
  super_admin: ['all_permissions']
};

const roleLabels = {
  installer: 'Installer',
  warehouse: 'Warehouse',
  manager: 'Manager',
  admin: 'Admin',
  sales_rep: 'Sales Rep',
  super_admin: 'Super Admin'
};

const roleColors = {
  installer: '#4CAF50',
  warehouse: '#FF9800',
  manager: '#2196F3',
  admin: '#9C27B0',
  sales_rep: '#E91E63',
  super_admin: '#F44336'
};

const roleIcons = {
  installer: <Build />,
  warehouse: <Warehouse />,
  manager: <SupervisorAccount />,
  admin: <AdminPanelSettings />,
  sales_rep: <SupportAgent />,
  super_admin: <Security />
};

const Settings: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'installer' as User['role'],
    status: 'active' as User['status'],
    password: ''
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  // Filter users based on search term, role, and status
  useEffect(() => {
    let filtered = [...users];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(user =>
        user.firstName.toLowerCase().includes(term) ||
        user.lastName.toLowerCase().includes(term) ||
        user.email.toLowerCase().includes(term) ||
        `${user.firstName} ${user.lastName}`.toLowerCase().includes(term)
      );
    }

    // Role filter
    if (roleFilter) {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    // Status filter
    if (statusFilter) {
      filtered = filtered.filter(user => user.status === statusFilter);
    }

    setFilteredUsers(filtered);
  }, [users, searchTerm, roleFilter, statusFilter]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const users = await mockApi.getUsers();
      setUsers(users as unknown as User[]);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = () => {
    setEditingUser(null);
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      role: 'installer',
      status: 'active',
      password: ''
    });
    setDialogOpen(true);
  };

  const handleEditUser = (user: User) => {
    setEditingUser(user);
    setFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      role: user.role,
      status: user.status,
      password: ''
    });
    setDialogOpen(true);
  };

  const handleDeleteUser = async (userId: string) => {
    if (window.confirm('Are you sure you want to delete this user?')) {
      try {
        await mockApi.deleteUser(userId);
        setUsers(users.filter(user => user._id !== userId));
        setSnackbar({ open: true, message: 'User deleted successfully', severity: 'success' });
      } catch (err: any) {
        setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to delete user', severity: 'error' });
      }
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setRoleFilter('');
    setStatusFilter('');
  };

  const handleSaveUser = async () => {
    try {
      if (editingUser) {
        // Update existing user
        const updateData: any = { ...formData };
        if (!updateData.password) {
          delete updateData.password; // Don't update password if empty
        }
        await mockApi.updateUser(editingUser._id, updateData);
        setSnackbar({ open: true, message: 'User updated successfully', severity: 'success' });
      } else {
        // Create new user
        await mockApi.createUser(formData);
        setSnackbar({ open: true, message: 'User created successfully', severity: 'success' });
      }
      setDialogOpen(false);
      fetchUsers();
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to save user', severity: 'error' });
    }
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, user: User) => {
    setMenuAnchor(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setSelectedUser(null);
  };

  const handleToggleStatus = async (user: User) => {
    try {
      const newStatus = user.status === 'active' ? 'inactive' : 'active';
      await mockApi.updateUser(user._id, { ...user, status: newStatus });
      setUsers(users.map(u => u._id === user._id ? { ...u, status: newStatus } : u));
      setSnackbar({ 
        open: true, 
        message: `User ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully`, 
        severity: 'success' 
      });
    } catch (err: any) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to update user status', severity: 'error' });
    }
    handleMenuClose();
  };

  const getStatusColor = (status: User['status']) => {
    switch (status) {
      case 'active': return 'success';
      case 'inactive': return 'error';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>Loading users...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ backgroundColor: '#1A1A1A', minHeight: '100vh', p: 3 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography
          variant="h4"
          sx={{
            color: '#D4AF37',
            fontWeight: 700,
          }}
        >
          ⚙️ Settings & User Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleAddUser}
          sx={{
            backgroundColor: '#D4AF37',
            color: '#000000',
            '&:hover': {
              backgroundColor: '#B8941F',
            },
          }}
        >
          Add User
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Search and Filter Controls */}
      <Card sx={{ mb: 3, backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
            🔍 Search & Filter Users
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: <Search sx={{ color: '#D4AF37', mr: 1 }} />,
                  endAdornment: searchTerm && (
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm('')}
                      sx={{ color: '#AAAAAA' }}
                    >
                      <Clear />
                    </IconButton>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#333333' },
                    '&:hover fieldset': { borderColor: '#D4AF37' },
                    '&.Mui-focused fieldset': { borderColor: '#D4AF37' },
                  },
                  '& .MuiInputLabel-root': { color: '#AAAAAA' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#D4AF37' },
                }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#AAAAAA' }}>Filter by Role</InputLabel>
                <Select
                  value={roleFilter}
                  label="Filter by Role"
                  onChange={(e) => setRoleFilter(e.target.value)}
                  sx={{
                    color: '#FFFFFF',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333333' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' },
                  }}
                >
                  <MenuItem value="">All Roles</MenuItem>
                  {Object.entries(roleLabels).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {roleIcons[value as keyof typeof roleIcons]}
                        {label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#AAAAAA' }}>Filter by Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Filter by Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                  sx={{
                    color: '#FFFFFF',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333333' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' },
                  }}
                >
                  <MenuItem value="">All Status</MenuItem>
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                startIcon={<Clear />}
                fullWidth
                sx={{
                  color: '#D4AF37',
                  borderColor: '#D4AF37',
                  '&:hover': {
                    borderColor: '#B8941F',
                    backgroundColor: 'rgba(212, 175, 55, 0.1)',
                  },
                }}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
          <Box mt={2} display="flex" alignItems="center" gap={2}>
            <Typography variant="body2" sx={{ color: '#AAAAAA' }}>
              Showing {filteredUsers.length} of {users.length} users
            </Typography>
            {(searchTerm || roleFilter || statusFilter) && (
              <Chip
                label={`${users.length - filteredUsers.length} filtered out`}
                size="small"
                sx={{
                  backgroundColor: 'rgba(212, 175, 55, 0.2)',
                  color: '#D4AF37',
                }}
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* User Management Card */}
      <Card sx={{ backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
            👥 User Management
          </Typography>
          
          <TableContainer component={Paper} sx={{ backgroundColor: '#1A1A1A', border: '1px solid #333333' }}>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#2C2C2C' }}>
                  <TableCell sx={{ color: '#D4AF37', fontWeight: 'bold' }}>User</TableCell>
                  <TableCell sx={{ color: '#D4AF37', fontWeight: 'bold' }}>Role</TableCell>
                  <TableCell sx={{ color: '#D4AF37', fontWeight: 'bold' }}>Status</TableCell>
                  <TableCell sx={{ color: '#D4AF37', fontWeight: 'bold' }}>Last Login</TableCell>
                  <TableCell sx={{ color: '#D4AF37', fontWeight: 'bold' }}>Created</TableCell>
                  <TableCell sx={{ color: '#D4AF37', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map((user) => (
                  <TableRow key={user._id} sx={{ '&:hover': { backgroundColor: 'rgba(212, 175, 55, 0.05)' } }}>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar sx={{ backgroundColor: roleColors[user.role], color: '#FFFFFF' }}>
                          {user.firstName.charAt(0)}{user.lastName.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography sx={{ color: '#FFFFFF', fontWeight: 500 }}>
                            {user.firstName} {user.lastName}
                          </Typography>
                          <Typography variant="body2" sx={{ color: '#AAAAAA' }}>
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={roleIcons[user.role]}
                        label={roleLabels[user.role]}
                        sx={{
                          backgroundColor: roleColors[user.role],
                          color: '#FFFFFF',
                          fontWeight: 500
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.status.charAt(0).toUpperCase() + user.status.slice(1)}
                        color={getStatusColor(user.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell sx={{ color: '#AAAAAA' }}>
                      {user.lastLogin ? formatDate(user.lastLogin) : 'Never'}
                    </TableCell>
                    <TableCell sx={{ color: '#AAAAAA' }}>
                      {formatDate(user.createdAt)}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => handleMenuClick(e, user)}
                        sx={{ color: '#D4AF37' }}
                      >
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Role Permissions Card */}
      <Card sx={{ mt: 3, backgroundColor: '#2C2C2C', border: '1px solid #333333' }}>
        <CardContent>
          <Typography variant="h6" sx={{ color: '#D4AF37', mb: 2 }}>
            🔐 Role Permissions
          </Typography>
          <Grid container spacing={2}>
            {Object.entries(rolePermissions).map(([role, permissions]) => (
              <Grid item xs={12} md={6} key={role}>
                <Box sx={{ p: 2, border: '1px solid #333333', borderRadius: 1 }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    {roleIcons[role as keyof typeof roleIcons]}
                    <Typography sx={{ color: '#D4AF37', fontWeight: 600 }}>
                      {roleLabels[role as keyof typeof roleLabels]}
                    </Typography>
                  </Box>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {permissions.map((permission: string) => (
                      <Chip
                        key={permission}
                        label={permission.replace(/_/g, ' ')}
                        size="small"
                        sx={{
                          backgroundColor: 'rgba(212, 175, 55, 0.2)',
                          color: '#D4AF37',
                          fontSize: '0.7rem'
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Card>

      {/* Add/Edit User Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#2C2C2C',
            border: '1px solid #333333',
            color: '#FFFFFF',
          }
        }}
      >
        <DialogTitle sx={{ color: '#D4AF37' }}>
          {editingUser ? 'Edit User' : 'Add New User'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#333333' },
                    '&:hover fieldset': { borderColor: '#D4AF37' },
                    '&.Mui-focused fieldset': { borderColor: '#D4AF37' },
                  },
                  '& .MuiInputLabel-root': { color: '#AAAAAA' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#D4AF37' },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#333333' },
                    '&:hover fieldset': { borderColor: '#D4AF37' },
                    '&.Mui-focused fieldset': { borderColor: '#D4AF37' },
                  },
                  '& .MuiInputLabel-root': { color: '#AAAAAA' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#D4AF37' },
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#333333' },
                    '&:hover fieldset': { borderColor: '#D4AF37' },
                    '&.Mui-focused fieldset': { borderColor: '#D4AF37' },
                  },
                  '& .MuiInputLabel-root': { color: '#AAAAAA' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#D4AF37' },
                }}
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#AAAAAA' }}>Role</InputLabel>
                <Select
                  value={formData.role}
                  label="Role"
                  onChange={(e) => setFormData({ ...formData, role: e.target.value as User['role'] })}
                  sx={{
                    color: '#FFFFFF',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333333' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' },
                  }}
                >
                  {Object.entries(roleLabels).map(([value, label]) => (
                    <MenuItem key={value} value={value}>
                      <Box display="flex" alignItems="center" gap={1}>
                        {roleIcons[value as keyof typeof roleIcons]}
                        {label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <FormControl fullWidth>
                <InputLabel sx={{ color: '#AAAAAA' }}>Status</InputLabel>
                <Select
                  value={formData.status}
                  label="Status"
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as User['status'] })}
                  sx={{
                    color: '#FFFFFF',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#333333' },
                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#D4AF37' },
                  }}
                >
                  <MenuItem value="active">Active</MenuItem>
                  <MenuItem value="inactive">Inactive</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={editingUser ? "New Password (leave blank to keep current)" : "Password"}
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: '#FFFFFF',
                    '& fieldset': { borderColor: '#333333' },
                    '&:hover fieldset': { borderColor: '#D4AF37' },
                    '&.Mui-focused fieldset': { borderColor: '#D4AF37' },
                  },
                  '& .MuiInputLabel-root': { color: '#AAAAAA' },
                  '& .MuiInputLabel-root.Mui-focused': { color: '#D4AF37' },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDialogOpen(false)}
            sx={{ color: '#FFFFFF', '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveUser}
            variant="contained"
            sx={{
              backgroundColor: '#D4AF37',
              color: '#000000',
              '&:hover': { backgroundColor: '#B8941F' },
            }}
          >
            {editingUser ? 'Update User' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* User Actions Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        sx={{
          '& .MuiPaper-root': {
            backgroundColor: '#2C2C2C',
            border: '1px solid #333333',
          },
        }}
      >
        <MenuItem onClick={() => selectedUser && handleEditUser(selectedUser)}>
          <ListItemIcon>
            <Edit fontSize="small" sx={{ color: '#D4AF37' }} />
          </ListItemIcon>
          <ListItemText>Edit User</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => selectedUser && handleToggleStatus(selectedUser)}>
          <ListItemIcon>
            {selectedUser?.status === 'active' ? 
              <Lock fontSize="small" sx={{ color: '#F44336' }} /> : 
              <LockOpen fontSize="small" sx={{ color: '#4CAF50' }} />
            }
          </ListItemIcon>
          <ListItemText>
            {selectedUser?.status === 'active' ? 'Deactivate' : 'Activate'}
          </ListItemText>
        </MenuItem>
        <MenuItem onClick={() => selectedUser && handleDeleteUser(selectedUser._id)}>
          <ListItemIcon>
            <Delete fontSize="small" sx={{ color: '#F44336' }} />
          </ListItemIcon>
          <ListItemText>Delete User</ListItemText>
        </MenuItem>
      </Menu>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
